/**
 * WorkReportDetailHeader — the wide hero card at the top of the report
 * detail page (D2f).
 *
 * Layout follows the reference design: a round amber-tinted document
 * icon on the left, the report title as the page's single h1 with a
 * small "Rapport" badge beneath it, and the primary action on the
 * right. On mobile the action wraps under the title rather than
 * shrinking the heading.
 *
 * The reference also shows an export button and an overflow menu. Both
 * are omitted: there is no work-report PDF export in this codebase and
 * a menu with nothing in it is worse than no menu. SAFE-DELETE1 put
 * "Rapport stornieren" here (deletion does not exist for reports); the
 * lists keep their own storno entry point through the shared dialog.
 *
 * The action is amber rather than `bg-primary`, which is blue in this
 * app: amber is the established work-report accent (the duration badge
 * everywhere else uses it) and the reference design is amber too. Both
 * themes get an explicit pairing, so neither inherits a surface that
 * only reads in one of them.
 */
import React from "react";
import { Ban, FileText, Pencil } from "lucide-react";

export default function WorkReportDetailHeader({
  title,
  onEditClick,
  /** REPORT-UX1: cancelled reports show a badge instead of actions —
   * editing and a second cancellation are backend-409s anyway, so the
   * buttons would be dead weight promising something refused. */
  isCancelled = false,
  onCancelClick,
  /** REPORT-PEOPLE: true when this booking was captured together with
   * others. Only that fact is stated — how many people, and who, would
   * need a query over the batch, and this page shows one person's
   * booking. The raw id is never rendered. */
  isJointCapture = false,
}) {
  return (
    <div className="mb-4 rounded-[12px] border border-border bg-card p-5 shadow-sm dark:border-[#1e2130] dark:bg-[#13161f] sm:p-6">
      <div className="flex flex-wrap items-start justify-between gap-4">
        <div className="flex min-w-0 items-center gap-4">
          <span
            aria-hidden="true"
            className="inline-flex h-12 w-12 shrink-0 items-center justify-center rounded-full bg-amber-500/15 text-amber-700 dark:text-amber-400 sm:h-14 sm:w-14"
          >
            <FileText className="h-6 w-6 sm:h-7 sm:w-7" />
          </span>
          <div className="min-w-0">
            <h1 className="m-0 break-words text-[22px] font-bold leading-tight tracking-tight text-foreground dark:text-[#e2e4ed] sm:text-[26px]">
              {title}
            </h1>
            <span className="mt-2 inline-flex items-center gap-2">
              <span className="inline-flex rounded-full bg-amber-500/15 px-2.5 py-0.5 text-[11px] font-semibold text-amber-700 dark:text-amber-400">
                Rapport
              </span>
              {isCancelled ? (
                <span className="inline-flex items-center gap-1 rounded-full border border-destructive/40 bg-destructive/10 px-2.5 py-0.5 text-[11px] font-semibold text-destructive dark:text-[#ff6b6b]">
                  <Ban className="h-3 w-3" aria-hidden="true" />
                  Storniert
                </span>
              ) : null}
              {isJointCapture ? (
                <span
                  className="inline-flex rounded-full border border-border bg-muted px-2.5 py-0.5 text-[11px] font-semibold text-muted-foreground"
                  title="Diese Personenbuchung wurde gemeinsam mit weiteren Rapporten erfasst."
                  data-testid="wr-detail-batch-badge"
                >
                  Gemeinsam erfasst
                </span>
              ) : null}
            </span>
          </div>
        </div>

        {isCancelled ? null : (
          <div className="flex shrink-0 flex-wrap items-center gap-2">
            {/* SAFE-DELETE1: the header's destructive action is
                STORNIEREN — deleting a report does not exist, and the
                UI must not promise it anywhere. */}
            {onCancelClick ? (
              <button
                type="button"
                onClick={onCancelClick}
                className="inline-flex items-center gap-2 rounded-[8px] border border-destructive/40 bg-transparent px-4 py-2.5 text-[13px] font-semibold text-destructive transition-colors hover:bg-destructive/10 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
              >
                <Ban className="h-4 w-4" aria-hidden="true" />
                Rapport stornieren
              </button>
            ) : null}
            <button
              type="button"
              onClick={onEditClick}
              className="inline-flex items-center gap-2 rounded-[8px] bg-amber-600 px-4 py-2.5 text-[13px] font-semibold text-white shadow-sm transition-colors hover:bg-amber-700 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 dark:bg-amber-500 dark:text-[#1a1205] dark:hover:bg-amber-400"
            >
              <Pencil className="h-4 w-4" aria-hidden="true" />
              Rapport bearbeiten
            </button>
          </div>
        )}
      </div>
    </div>
  );
}
